// What a native shell (apps/mobile, Capacitor) gives the guardian and teacher screens. The
// screens never import Capacitor: the shell passes these in, and the web passes the defaults.
// That keeps Capacitor out of the web bundle and lets tests stub every native call.
import { forgetOffline } from "./offline.ts";
import type { RestoreKeyBridge } from "./restore.ts";
import type { RadioEngine, RadioOptions } from "./radio/radio.ts";

/** Where the session token lives. Native: Keychain / Android Keystore. Tests: memory. */
export interface SecretBackend {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

export interface SharePayload { title?: string; text: string; url?: string }

export interface PushBridge {
  /**
   * Asks permission if needed and returns the FCM token, or null if the person said no. On
   * Android it also creates the app's notification channels ("handovers" for parents,
   * "requests" for staff, "alerts" for both), which the server names on every push.
   */
  register(app?: "guardian" | "teacher"): Promise<{ token: string; platform: "ios" | "android" } | null>;
  permission(): Promise<"granted" | "denied" | "prompt">;
  /** A tap on a notification; returns an unsubscribe function. */
  onTap?(cb: (data: Record<string, string>) => void): () => void;
  /** A push that arrived while the app is open (Android shows nothing then); returns an unsubscribe function. */
  onForeground?(cb: (n: { title: string; body: string; data: Record<string, string> }) => void): () => void;
}

export interface KioskLockStatus {
  /** The app is the tablet's device owner (docs/ops/kiosk-setup.md §4). */
  owner: boolean;
  /** Android lets it lock itself without asking (device owner, or an EMM allowed it). */
  permitted: boolean;
  mode: "none" | "pinned" | "locked";
  /** Staff unlocked it from the Staff menu; it locks again when they leave the menu. */
  maintenance: boolean;
}

/** The door tablet's lock (Android lock task, PMU-165). */
export interface KioskLockBridge {
  status(): Promise<KioskLockStatus>;
  /** Arms the lock and locks (permitted) or asks Android to pin the app (otherwise). */
  lock(): Promise<void>;
  /** Out of the lock for maintenance, until lock() or a restart of the app. */
  unlock(): Promise<void>;
  /** Back to enrolment: unlocked and not locked again until lock(). */
  disarm(): Promise<void>;
  openSettings(): Promise<void>;
  /** Stops being device owner: no lock, no home screen. */
  release(): Promise<void>;
}

/** The bus phone's location during a run (Android: a background service; transport slice 4). */
export interface BusTrackerBridge {
  /** Asks for location if needed and shares it until stop() or the run ends. */
  start(o: { url: string; token: string; label: string }): Promise<void>;
  stop(): Promise<void>;
  /** Each post: `status` is the HTTP status (0 when stopped), `fixAt` the fix's time. Returns unsubscribe. */
  onState(cb: (s: { tracking: boolean; status: number; fixAt?: number; accuracy?: number; locationOff?: boolean }) => void): () => void;
}

/** Reads a parent's NFC pickup pass (Android: the staff app, a door tablet with NFC). */
export interface NfcReaderBridge {
  available(): Promise<boolean>;
  /** Listens while a pass scanner is open; `onText` gets the same text a QR carries. Returns stop. */
  listen(onText: (text: string) => void, onError?: (message: string) => void, onSent?: () => void): Promise<() => void>;
  /** A door code to hand to the parent's phone on a tap (instead of reading their pass); null to read. */
  setDoor(code: string | null): Promise<void>;
}

/** The parent's phone as an NFC pickup pass (Android only). */
export interface NfcPassBridge {
  status(): Promise<{ supported: boolean; enabled: boolean }>;
  /** Signed pass text for upcoming 30 s windows; the card service answers with the current one. */
  setPasses(passes: { window: number; text: string }[]): Promise<void>;
  /** While the pass is on screen: act only as a card, stop polling as a reader (a phone's own polling drops the tap). */
  setCardOnly(on: boolean): Promise<void>;
  /** A staff phone tapped this one and gave its door code: `cb` gets it. Returns unsubscribe. */
  onDoor(cb: (code: string) => void): () => void;
  /** A door code tapped in the last minute while the app was closed, once. */
  takeDoor(): Promise<string | null>;
  clear(): Promise<void>;
}

export interface AppPlatform {
  kind: "web" | "ios" | "android";
  /** null on the web: the session is an HttpOnly cookie there. */
  tokens: TokenStore | null;
  share?(p: SharePayload): Promise<void>;
  push?: PushBridge;
  /** Universal link / App Link opened while the app runs or launched it (e.g. /auth/magic#token). */
  onDeepLink?(cb: (url: string) => void): () => void;
  /** WebAuthn works on the web; the native shells need a passkey bridge first (docs/spec/apps.md). */
  passkeys: boolean;
  appVersion: string;
  /** API origin, "" when same-origin (the web). */
  apiBase: string;
  /**
   * A native staff-radio engine (Android: a background service, with Volume Up as the talk key
   * even when the app is in the background). Absent: the web engine, foreground only.
   */
  radioEngine?: RadioEngineFactory;
  nfcReader?: NfcReaderBridge;
  nfcPass?: NfcPassBridge;
  busTracker?: BusTrackerBridge;
  /** The drive screen's native parts (Android): spoken directions and the screen kept on. Absent: the browser's, if any. */
  drive?: DriveBridge;
  /** Android: Credential Manager restore keys (shared/restore.ts). */
  restoreKey?: RestoreKeyBridge;
  /** Android staff app: finds the school's door tablet on the Wi-Fi and talks to it (offline phase 2, PMU-271). */
  lanLink?: LanLinkBridge;
  /** Android: the emergency mesh over Bluetooth (PMU-273). */
  mesh?: MeshBridge;
}

/** The native emergency mesh (android-src/mesh/MeshPlugin). */
export interface MeshBridge {
  start(o: { self: string; siteId: string; router: boolean; mode: "standby" | "full"; peers: { id: string; kind: string; pubkey: string }[] }): Promise<void>;
  setMode(mode: "standby" | "full"): Promise<void>;
  send(packet: string): Promise<void>;
  status(): Promise<Record<string, unknown>>;
  drain(): Promise<string[]>;
  stop(): Promise<void>;
  onPacket(cb: (packet: string) => void): () => void;
  /** For notifications while the app is away: the day keys, this phone's member of staff, staff names. */
  setInfo?(o: { keys: { date: string; key: string }[]; staffId: string; names: Record<string, string> }): Promise<void>;
}

/** The phone's side of the door-as-hub links: NSD discovery and one socket per door, one line per frame. */
export interface LanLinkBridge {
  discover(siteId: string): Promise<void>;
  connect(host: string, port: number, conn: string): Promise<void>;
  send(conn: string, text: string): Promise<void>;
  close(conn: string): Promise<void>;
  stop(): Promise<void>;
  /** Keeps the app running in the background while the internet is down (a foreground service). */
  keepAlive?(on: boolean): Promise<void>;
  /** A local notification: a request at the door while the app is in the background. */
  alert?(tag: string, title: string, body: string): Promise<void>;
  on(event: "found", cb: (d: { host: string; port: number; door: string }) => void): () => void;
  on(event: "open" | "close", cb: (d: { conn: string }) => void): () => void;
  on(event: "message", cb: (d: { conn: string; text: string }) => void): () => void;
}

/** The door tablet's side: a socket server announced on the Wi-Fi. */
export interface LanHubBridge {
  start(siteId: string, doorId: string): Promise<{ port: number }>;
  /** Offline phase 3: the door's radio relay under this key (base64url, 32 bytes); its port. */
  radioStart?(key: string): Promise<{ port: number }>;
  radioStop?(): Promise<void>;
  send(conn: string, text: string): Promise<void>;
  close(conn: string): Promise<void>;
  stop(): Promise<void>;
  on(event: "open" | "close", cb: (d: { conn: string }) => void): () => void;
  on(event: "message", cb: (d: { conn: string; text: string }) => void): () => void;
  /** The radio relay's events (turns, joins, leaves, refusals), one flat JSON object each. */
  on(event: "radioLog", cb: (d: { json: string }) => void): () => void;
}

export interface DriveBridge { speak(text: string): Promise<void>; stop(): Promise<void>; keepScreenOn(on: boolean): Promise<void> }

export type RadioEngineFactory = (o: RadioOptions) => RadioEngine;

export const webPlatform = (over: Partial<AppPlatform> = {}): AppPlatform => ({
  kind: "web", tokens: null, passkeys: true, appVersion: "web", apiBase: "",
  share: typeof navigator !== "undefined" && "share" in navigator ? (p) => navigator.share(p) : undefined,
  ...over,
});

/**
 * The app session token: kept in secure storage, cached in memory so `configureApi({ getToken })`
 * stays synchronous. An expired token is dropped on load rather than sent and refused.
 */
export class TokenStore {
  private backend: SecretBackend;
  private key: string;
  private value: { token: string; expires_at: number } | null = null;
  private listeners = new Set<() => void>();
  loaded = false;

  constructor(backend: SecretBackend, key = "pmu.session") { this.backend = backend; this.key = key; }

  get token(): string | null { return this.value && this.value.expires_at > Date.now() ? this.value.token : null; }

  async load(now = Date.now()): Promise<string | null> {
    const raw = await this.backend.get(this.key).catch(() => null);
    let v: { token: string; expires_at: number } | null = null;
    try { v = raw ? JSON.parse(raw) : null; } catch { v = null; }
    if (v && (typeof v.token !== "string" || !(v.expires_at > now))) { v = null; await this.backend.remove(this.key).catch(() => undefined); }
    this.value = v;
    this.loaded = true;
    this.emit();
    return v?.token ?? null;
  }

  async save(token: string, expiresAt: number) {
    this.value = { token, expires_at: expiresAt };
    await this.backend.set(this.key, JSON.stringify(this.value));
    this.emit();
  }

  async clear() {
    this.value = null;
    await this.backend.remove(this.key).catch(() => undefined);
    // Signed out: nothing this account loaded or queued offline stays on the device (PMU-270).
    await forgetOffline().catch(() => undefined);
    this.emit();
  }

  subscribe(fn: () => void): () => void { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; }
  private emit() { this.listeners.forEach((l) => l()); }
}

export const memoryBackend = (): SecretBackend => {
  const m = new Map<string, string>();
  return { get: async (k) => m.get(k) ?? null, set: async (k, v) => { m.set(k, v); }, remove: async (k) => { m.delete(k); } };
};
